// =====================================================================
// Anonymized LIST view for the agent-request feed (card ↔ list toggle) — V2
// UI rebuild 1:1 from design doc 01 ("List view": same data, denser scan).
//
// Columns: Type · Status | Role · Country | Figures | Timing | Agency |
// actions (expand chevron · bookmark · Pitch). Clicking a row (or its
// chevron button) expands it to the FULL description + key requirements —
// the artboard's only reading affordance here; the loupe lives on the cards.
// Desktop-only surface — ≥1280; below that the feed keeps cards (page-side).
//
// Artboard rules honored verbatim: 150/210+/1.3fr/200/220/118 column grid,
// 14px gaps, 13x18 row padding, image flags 16x11.5 radius 2, EMPTY VALUES
// RENDER NOTHING (no "-"), dashed expanded panel indented to 44px.
//
// ANONYMITY (Brief Rule 4/8): the club is NEVER shown here — no name, logo,
// city, or division-country. The only identities are the posting agency +
// responsible agent; location stays coarse (country + league tier). The
// figures shown are the same REAL public figures as on the card (no band).
// =====================================================================
import React, { useState } from "react";
import Avatar from "../common/Avatar";
import ImageOrInitials from "../agency/ImageOrInitials";
import ClubVerifiedBadge from "./ClubVerifiedBadge";
import FlagImg from "./FlagImg";
import { BookmarkGlyph, CalendarGlyph } from "./glyphs";
import {
  formatPositionValue,
  formatStaffRole,
  formatTypeOfTender,
  formatLeagueTier,
} from "../../constants/agentTenderChoices";
import {
  buildFinRows,
  getCommission,
  getPeriodLine,
} from "../../utils/agentRequestFigures";

// Title for a request row: the staff role for staff requests, else the position.
const requestTitle = (t) =>
  t.tender_category === "staff"
    ? formatStaffRole(t.staff_role, t.other_staff_position)
    : formatPositionValue(t.position, t.other_staff_position);

const typePillClass = (type) => {
  if (type === "buy") return "bg-[#E8F1FF] text-[#1B6FE0]";
  if (type === "loan") return "bg-[#EEF2FF] text-[#4F46E5]";
  if (type === "trial") return "bg-[#FEF3C7] text-[#B45309]";
  return "bg-[#F1F5F9] text-[#334155]";
};

// The artboard's row grid — header and rows share it exactly.
const ROW_GRID =
  "grid grid-cols-[150px_minmax(210px,1.1fr)_1.3fr_200px_220px_118px] gap-3.5 items-center";

// Row-expand chevron (verbatim artboard glyph, 26px bordered button).
const RowChevronGlyph = ({ open }) => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    className={`transition-transform ${open ? "rotate-180" : ""}`}
    aria-hidden="true"
  >
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const HeaderCell = ({ children, className = "" }) => (
  <span
    className={`text-[10.5px] font-bold tracking-[0.06em] text-[#94A3B8] uppercase ${className}`}
  >
    {children}
  </span>
);

// Figures cell: the fin rows + commission three-state, one line each.
const FiguresCell = ({ tender }) => {
  const rows = buildFinRows(tender, "mo");
  const commission = getCommission(tender);
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      {rows.map((r) => (
        <div key={r.key} className="text-[12.5px] whitespace-nowrap overflow-hidden text-ellipsis">
          <span className="text-[#64748B]">{r.label}</span>{" "}
          <span className="font-semibold tabular-nums text-[#0F172A]">{r.value}</span>
        </div>
      ))}
      {rows.length === 0 && (
        <div className="text-[12.5px] text-[#475569]">Conditions negotiable</div>
      )}
      {commission.state === "none" ? (
        <div
          className="text-[12px] font-semibold text-[#B45309]"
          title="The posting agency won't pay or share a commission on this deal."
        >
          No commission for this job
        </div>
      ) : (
        <div className="text-[12px] whitespace-nowrap">
          <span className="text-[#64748B]">Commission</span>{" "}
          <span
            className={`font-semibold ${
              commission.state === "split" ? "text-[#0F172A]" : "text-[#64748B]"
            }`}
          >
            {commission.state === "split" ? commission.value : "Negotiable"}
          </span>
        </div>
      )}
    </div>
  );
};

const AgentRequestListView = ({
  tenders,
  onSendPlayer,
  onToggleSave,
  savingIds = new Set(),
  newestId,
}) => {
  const [expanded, setExpanded] = useState(null);
  const toggle = (id) => setExpanded((cur) => (cur === id ? null : id));

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl overflow-x-auto mt-3.5 shadow-sm">
      <div className="min-w-[980px]">
        {/* Header row */}
        <div className={`${ROW_GRID} px-[18px] py-[9px] bg-[#F8FAFC] border-b border-[#EEF2F7]`}>
          <HeaderCell>Type · Status</HeaderCell>
          <HeaderCell>Role · Country</HeaderCell>
          <HeaderCell>Figures</HeaderCell>
          <HeaderCell>Timing</HeaderCell>
          <HeaderCell>Agency</HeaderCell>
          <span />
        </div>

        {tenders.map((tender) => {
          const isOpen = expanded === tender.id;
          const title = requestTitle(tender);
          const countryText = [
            tender.country?.name,
            formatLeagueTier(tender.league_tier),
          ]
            .filter(Boolean)
            .join(" · ");
          const period = getPeriodLine(tender);
          const isStaff = tender.tender_category === "staff";
          const agentStats = tender.trust_stats?.agent;
          return (
            <React.Fragment key={tender.id}>
              <div
                onClick={() => toggle(tender.id)}
                className={`${ROW_GRID} px-[18px] py-[13px] border-b border-[#F1F5F9] cursor-pointer transition ${
                  isOpen ? "bg-[#F8FAFC]/60" : "bg-white hover:bg-[#F8FAFC]"
                }`}
              >
                {/* Type + verification status, stacked */}
                <div className="flex flex-col gap-[5px] items-start">
                  <span
                    className={`text-[10.5px] font-bold px-2 py-0.5 rounded-full capitalize ${typePillClass(
                      tender.type_of_tender,
                    )}`}
                  >
                    {formatTypeOfTender(tender.type_of_tender)}
                  </span>
                  <ClubVerifiedBadge status={tender.verification_status} />
                </div>

                {/* Role + level + country line — empty values render NOTHING */}
                <div className="min-w-0">
                  <div className="flex items-baseline gap-[7px] min-w-0">
                    {/* No `capitalize`: staff role "other" carries the
                        agent's own typed title (T059). */}
                    {title && (
                      <span className="text-[14px] font-bold text-[#0F172A] truncate">
                        {title}
                      </span>
                    )}
                    {tender.recruiting_for && (
                      <span
                        className={`flex-none text-[10.5px] font-semibold px-[7px] py-px rounded-full ${
                          tender.recruiting_for === "youth"
                            ? "bg-[#E8F1FF] text-[#1B6FE0]"
                            : "bg-[#F1F5F9] text-[#475569]"
                        }`}
                      >
                        {tender.recruiting_for === "youth" ? "Youth" : "Professional"}
                      </span>
                    )}
                    {newestId != null && tender.id === newestId && (
                      <span className="flex-none px-2 py-0.5 bg-[#2283FF] text-white text-[10px] font-semibold rounded-full whitespace-nowrap">
                        NEWEST
                      </span>
                    )}
                  </div>
                  {countryText && (
                    <div className="flex items-center gap-1.5 text-[12px] text-[#475569] mt-[3px]">
                      <FlagImg
                        country={tender.country}
                        className="w-4 h-[11.5px] rounded-[2px]"
                      />
                      {countryText}
                    </div>
                  )}
                </div>

                {/* Real figures (never the band) */}
                <FiguresCell tender={tender} />

                {/* Timing: the period line */}
                <div className="flex flex-col gap-[3px]">
                  {period && (
                    <div className="flex items-center gap-[5px] text-[11.5px] text-[#64748B]">
                      <CalendarGlyph size={11} />
                      <span className="whitespace-nowrap">{period}</span>
                    </div>
                  )}
                </div>

                {/* Agency + agent + trust (NEVER the club) */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="flex-none inline-flex items-center">
                    {tender.posted_agency?.logo_url ? (
                      <img
                        src={tender.posted_agency.logo_url}
                        alt={tender.posted_agency?.name || "Agency"}
                        className="w-[26px] h-[26px] object-contain rounded-lg bg-white border border-[#E2E8F0]"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <ImageOrInitials src={null} alt={tender.posted_agency?.name} size="small" />
                    )}
                    {tender.responsible_agent && (
                      <span className="-ml-[7px] ring-2 ring-white rounded-full flex-none">
                        <Avatar
                          src={tender.responsible_agent.avatar}
                          fallbackText={tender.responsible_agent.display_name}
                          size={26}
                        />
                      </span>
                    )}
                  </span>
                  <div className="min-w-0">
                    {tender.posted_agency?.name && (
                      <div className="text-[12px] font-semibold text-[#0F172A] truncate">
                        {tender.posted_agency.name}
                      </div>
                    )}
                    <div className="text-[11px] text-[#64748B] truncate">
                      {tender.responsible_agent?.display_name}
                      {agentStats && (
                        <>
                          {tender.responsible_agent?.display_name && " · "}
                          {agentStats.posted ?? 0} posted{" · "}
                          {agentStats.verified ?? 0} verified
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Actions — chevron · bookmark · Pitch (artboard order) */}
                <div className="flex items-center justify-end gap-1.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggle(tender.id);
                    }}
                    className="w-[26px] h-[26px] border border-[#E2E8F0] rounded-lg inline-flex items-center justify-center text-[#64748B] bg-white hover:bg-gray-50"
                    aria-label={isOpen ? "Collapse row" : "Expand row"}
                    aria-expanded={isOpen}
                  >
                    <RowChevronGlyph open={isOpen} />
                  </button>
                  {onToggleSave && (
                    <button
                      type="button"
                      disabled={savingIds.has(tender.id)}
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggleSave(tender);
                      }}
                      className={`w-[26px] h-[26px] rounded-lg inline-flex items-center justify-center border ${
                        tender.is_saved
                          ? "border-[#BFDBFE] bg-[#E8F1FF] text-[#2283FF]"
                          : "border-[#E2E8F0] bg-white text-[#64748B] hover:bg-gray-50"
                      } ${savingIds.has(tender.id) ? "opacity-50 cursor-not-allowed" : ""}`}
                      aria-label={tender.is_saved ? "Unsave request" : "Save request"}
                      title={tender.is_saved ? "Unsave" : "Save"}
                    >
                      <BookmarkGlyph saved={!!tender.is_saved} size={12} />
                    </button>
                  )}
                  {tender.is_own_agency ? (
                    <span className="flex-none bg-[#E8F1FF] text-[#1B6FE0] text-[11px] font-bold px-2.5 py-[7px] rounded-lg whitespace-nowrap">
                      Your request
                    </span>
                  ) : (
                    onSendPlayer && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSendPlayer(tender);
                        }}
                        className="flex-none px-[11px] py-[7px] bg-[#2283FF] text-white rounded-lg hover:bg-[#1B6FE0] text-[11.5px] font-semibold whitespace-nowrap"
                        title={isStaff ? "Pitch candidate" : "Pitch player"}
                      >
                        Pitch
                      </button>
                    )
                  )}
                </div>
              </div>

              {/* Expanded row: description + key requirements in FULL.
                  Sections without content render nothing (artboard rule). */}
              {isOpen && (tender.description || tender.key_requirements) && (
                <div className="flex gap-6 pl-11 pr-[18px] pt-3 pb-4 bg-[#F8FAFC] border-b border-dashed border-[#E2E8F0]">
                  {tender.description && (
                    <div className="flex-1 min-w-0">
                      <div className="text-[10.5px] font-bold tracking-[0.06em] text-[#94A3B8] uppercase">
                        Description
                      </div>
                      <div className="text-[12.5px] text-[#334155] leading-relaxed mt-1 whitespace-pre-wrap break-words">
                        {tender.description}
                      </div>
                    </div>
                  )}
                  {tender.key_requirements && (
                    <div className="flex-1 min-w-0">
                      <div className="text-[10.5px] font-bold tracking-[0.06em] text-[#94A3B8] uppercase">
                        Key requirements
                      </div>
                      <div className="text-[12.5px] text-[#334155] leading-relaxed mt-1 whitespace-pre-wrap break-words">
                        {tender.key_requirements}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default AgentRequestListView;
